import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import "./Signup.css";

// Utility functions for cookie management
const setCookie = (name, value, days = 7) => {
  const expirationDate = new Date();
  expirationDate.setDate(expirationDate.getDate() + days);
  
  document.cookie = `${name}=${value}; expires=${expirationDate.toUTCString()}; path=/; secure; samesite=strict`;
};

const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

const deleteCookie = (name) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

function Signup() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    employeeName: '',
    email: '',
    password: '',
    confirmPassword: '',
    contactNumber: '',
    dateOfBirth: '',
    gender: '',
    address: '',
    profilePicture: null,
    role: 'Employee',
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [profilePreview, setProfilePreview] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });

  // Check if user is already logged in and redirect to dashboard
  useEffect(() => {
    const checkAuthStatus = () => {
      // Check for auth token in cookies
      const authToken = getCookie('authToken');
      
      // Check for employee data in localStorage
      const employeeData = localStorage.getItem('employeeData');
      
      // If both token and employee data exist, user is likely logged in
      if (authToken && employeeData) {
        try {
          // Verify the employee data is valid JSON
          JSON.parse(employeeData);
          
          // Redirect to dashboard
          navigate('/dashboard');
        } catch (error) {
          // If employee data is corrupted, clear it
          localStorage.removeItem('employeeData');
          deleteCookie('authToken');
        }
      }
    };

    checkAuthStatus();
  }, [navigate]);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validatePassword = (password) => {
    const strength = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };
    setPasswordStrength(strength);
    return Object.values(strength).filter(Boolean).length >= 4;
  };

  const validatePhoneNumber = (phone) => {
    const phoneRegex = /^[+]?[1-9][\d]{0,15}$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
  };

  const validateDateOfBirth = (dob) => {
    if (!dob) return true; // Optional field
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age >= 18 && age <= 100; // Must be between 18 and 100 years old
  };

  const validateProfilePicture = (file) => {
    if (!file) return true; // Optional field
    
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    const maxSize = 5 * 1024 * 1024; // 5MB
    
    if (!allowedTypes.includes(file.type)) {
      return false;
    }
    
    if (file.size > maxSize) {
      return false;
    }
    
    return true;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    
    if (name === 'password') {
      validatePassword(value);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    
    if (file) {
      if (!validateProfilePicture(file)) {
        setError("Profile picture must be a valid image file (JPEG, PNG, GIF) and less than 5MB.");
        return;
      }
      
      setForm({ ...form, profilePicture: file });
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfilePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    } else {
      setForm({ ...form, profilePicture: null });
      setProfilePreview(null);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setSuccess("");

    // Validation
    if (!form.employeeName || !form.email || !form.password || !form.confirmPassword) {
      setError("Please fill in all required fields.");
      setIsLoading(false);
      return;
    }

    if (!validateEmail(form.email)) {
      setError("Please enter a valid email address.");
      setIsLoading(false);
      return;
    }

    if (!validatePassword(form.password)) {
      setError("Password must meet the security requirements.");
      setIsLoading(false);
      return;
    }

    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match.");
      setIsLoading(false);
      return;
    }

    if (form.contactNumber && !validatePhoneNumber(form.contactNumber)) {
      setError("Please enter a valid contact number.");
      setIsLoading(false);
      return;
    }

    if (form.dateOfBirth && !validateDateOfBirth(form.dateOfBirth)) {
      setError("You must be at least 18 years old to register.");
      setIsLoading(false);
      return;
    }

    if (form.profilePicture && !validateProfilePicture(form.profilePicture)) {
      setError("Profile picture must be a valid image file (JPEG, PNG, GIF) and less than 5MB.");
      setIsLoading(false);
      return;
    }

    try {
      // Create FormData object for multipart/form-data
      const formData = new FormData();
      
      // Append all form fields
      formData.append('employeeName', form.employeeName);
      formData.append('email', form.email);
      formData.append('password', form.password);
      formData.append('contactNumber', form.contactNumber || '');
      formData.append('role', form.role);
      formData.append('DOB', form.dateOfBirth || '');
      formData.append('gender', form.gender || '');
      formData.append('address', form.address || '');
      
      // Append profile picture if selected
      if (form.profilePicture) {
        formData.append('profilePicture', form.profilePicture);
      }

      // Make API call
      const response = await fetch('http://127.0.0.1:5002/employee-auth/register', {
        method: 'POST',
        body: formData,
        // Don't set Content-Type header - let browser set it with boundary for multipart/form-data
      });

      const result = await response.json();

      if (response.ok) {
        // Success
        setSuccess(result.message || "Account created successfully! Please check your email to verify your account.");
        
        // Save token in cookies if provided
        if (result.token) {
          setCookie('authToken', result.token);
          
          // Optionally save employee data in localStorage for quick access
          if (result.employee) {
            localStorage.setItem('employeeData', JSON.stringify(result.employee));
          }
          
          console.log('Authentication token saved successfully');
        }
        
        // Reset form
        setForm({
          employeeName: '',
          email: '',
          password: '',
          confirmPassword: '',
          contactNumber: '',
          dateOfBirth: '',
          gender: '',
          address: '',
          profilePicture: null,
          role: 'Employee',
        });
        setProfilePreview(null);
        setShowPassword(false);
        setShowConfirmPassword(false);
        setPasswordStrength({
          length: false,
          uppercase: false,
          lowercase: false,
          number: false,
          special: false
        });

        // Clear file input
        const fileInput = document.getElementById('profilePicture');
        if (fileInput) {
          fileInput.value = '';
        }
        
        // Optional: Redirect to dashboard or login page after successful registration
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else {
        // Error from server
        setError(result.message || result.error || "Registration failed. Please try again.");
      }
    } catch (error) {
      console.error('Registration error:', error);
      
      // Handle different types of errors
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        setError("Unable to connect to the server. Please check your internet connection and try again.");
      } else if (error.name === 'AbortError') {
        setError("Request timed out. Please try again.");
      } else {
        setError("An unexpected error occurred. Please try again later.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getPasswordStrengthText = () => {
    const validCount = Object.values(passwordStrength).filter(Boolean).length;
    if (validCount === 0) return "";
    if (validCount < 3) return "Weak";
    if (validCount < 5) return "Good";
    return "Strong";
  };

  const getPasswordStrengthColor = () => {
    const validCount = Object.values(passwordStrength).filter(Boolean).length;
    if (validCount < 3) return "#f87171";
    if (validCount < 5) return "#fbbf24";
    return "#10b981";
  };

  return (
    <div className="tsr-signup-container">
      <form className="tsr-signup-form" onSubmit={handleSubmit}>
        <div className="tsr-signup-header">
          <h1 className="tsr-signup-brand">TrillionStock Research</h1>
          <h2 className="tsr-signup-title">Create Account</h2>
          <p className="tsr-signup-subtitle">..</p>
        </div>

        {error && <div className="tsr-signup-error">{error}</div>}
        {success && <div className="tsr-signup-success">{success}</div>}
        
        <div className="tsr-form-grid">
          <div className="tsr-signup-field full-width">
            <label htmlFor="employeeName">Full Name<span>*</span></label>
            <div className="tsr-input-wrapper">
              <input
                type="text"
                id="employeeName"
                name="employeeName"
                value={form.employeeName}
                onChange={handleChange}
                placeholder="Enter your full name"
                autoComplete="name"
                required
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="tsr-signup-field full-width">
            <label htmlFor="email">Email Address<span>*</span></label>
            <div className="tsr-input-wrapper">
              <input
                type="email"
                id="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="Enter your email address"
                autoComplete="username"
                required
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="tsr-signup-field">
            <label htmlFor="password">Password<span>*</span></label>
            <div className="tsr-input-wrapper tsr-password-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={form.password}
                onChange={handleChange}
                placeholder="Create a strong password"
                autoComplete="new-password"
                required
                disabled={isLoading}
              />
              <button
                type="button"
                className="tsr-password-toggle"
                onClick={togglePasswordVisibility}
                disabled={isLoading}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.99902 3L20.999 21M9.8433 9.91364C9.32066 10.4536 8.99902 11.1892 8.99902 12C8.99902 13.6569 10.3422 15 11.999 15C12.8215 15 13.5667 14.669 14.1086 14.133M6.49902 6.64715C4.59972 7.90034 3.15305 9.78394 2.45703 12C3.73128 16.0571 7.52159 19 11.9992 19C13.9881 19 15.8414 18.4194 17.3988 17.4184M10.999 5.04939C11.328 5.01673 11.6617 5 11.9992 5C16.4769 5 20.2672 7.94291 21.5414 12C21.2607 12.894 20.8577 13.7338 20.3522 14.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.45703 12C3.73128 7.94288 7.52159 5 11.9992 5C16.4769 5 20.2672 7.94291 21.5414 12C20.2672 16.0571 16.4769 19 11.9992 19C7.52159 19 3.73128 16.0571 2.45703 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M11.9992 15C13.6561 15 14.9992 13.6569 14.9992 12C14.9992 10.3431 13.6561 9 11.9992 9C10.3424 9 8.99924 10.3431 8.99924 12C8.99924 13.6569 10.3424 15 11.9992 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </button>
            </div>
            {form.password && (
              <div className="tsr-password-requirements">
                <div style={{ color: getPasswordStrengthColor(), fontWeight: '600', marginBottom: '0.25rem' }}>
                  {getPasswordStrengthText()} Password
                </div>
                <div style={{ color: passwordStrength.length ? '#10b981' : '#64748b' }}>
                  ✓ At least 8 characters
                </div>
                <div style={{ color: passwordStrength.uppercase ? '#10b981' : '#64748b' }}>
                  ✓ One uppercase letter
                </div>
                <div style={{ color: passwordStrength.lowercase ? '#10b981' : '#64748b' }}>
                  ✓ One lowercase letter
                </div>
                <div style={{ color: passwordStrength.number ? '#10b981' : '#64748b' }}>
                  ✓ One number
                </div>
                <div style={{ color: passwordStrength.special ? '#10b981' : '#64748b' }}>
                  ✓ One special character
                </div>
              </div>
            )}
          </div>

          <div className="tsr-signup-field">
            <label htmlFor="confirmPassword">Confirm Password<span>*</span></label>
            <div className="tsr-input-wrapper tsr-password-wrapper">
              <input
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                name="confirmPassword"
                value={form.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm your password"
                autoComplete="new-password"
                required
                disabled={isLoading}
              />
              <button
                type="button"
                className="tsr-password-toggle"
                onClick={toggleConfirmPasswordVisibility}
                disabled={isLoading}
                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
              >
                {showConfirmPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.99902 3L20.999 21M9.8433 9.91364C9.32066 10.4536 8.99902 11.1892 8.99902 12C8.99902 13.6569 10.3422 15 11.999 15C12.8215 15 13.5667 14.669 14.1086 14.133M6.49902 6.64715C4.59972 7.90034 3.15305 9.78394 2.45703 12C3.73128 16.0571 7.52159 19 11.9992 19C13.9881 19 15.8414 18.4194 17.3988 17.4184M10.999 5.04939C11.328 5.01673 11.6617 5 11.9992 5C16.4769 5 20.2672 7.94291 21.5414 12C21.2607 12.894 20.8577 13.7338 20.3522 14.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.45703 12C3.73128 7.94288 7.52159 5 11.9992 5C16.4769 5 20.2672 7.94291 21.5414 12C20.2672 16.0571 16.4769 19 11.9992 19C7.52159 19 3.73128 16.0571 2.45703 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M11.9992 15C13.6561 15 14.9992 13.6569 14.9992 12C14.9992 10.3431 13.6561 9 11.9992 9C10.3424 9 8.99924 10.3431 8.99924 12C8.99924 13.6569 10.3424 15 11.9992 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div className="tsr-signup-field">
            <label htmlFor="dateOfBirth">Date of Birth</label>
            <div className="tsr-input-wrapper">
              <input
                type="date"
                id="dateOfBirth"
                name="dateOfBirth"
                value={form.dateOfBirth}
                onChange={handleChange}
                max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                min={new Date(new Date().setFullYear(new Date().getFullYear() - 100)).toISOString().split('T')[0]}
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="tsr-signup-field">
            <label htmlFor="gender">Gender</label>
            <div className="tsr-input-wrapper">
              <select
                id="gender"
                name="gender"
                value={form.gender}
                onChange={handleChange}
                disabled={isLoading}
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
                <option value="prefer-not-to-say">Prefer not to say</option>
              </select>
            </div>
          </div>

          <div className="tsr-signup-field">
            <label htmlFor="contactNumber">Contact Number</label>
            <div className="tsr-input-wrapper">
              <input
                type="tel"
                id="contactNumber"
                name="contactNumber"
                value={form.contactNumber}
                onChange={handleChange}
                placeholder="Enter your contact number"
                autoComplete="tel"
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="tsr-signup-field full-width">
            <label htmlFor="address">Address</label>
            <div className="tsr-input-wrapper">
              <textarea
                id="address"
                name="address"
                value={form.address}
                onChange={handleChange}
                placeholder="Enter your full address"
                rows="3"
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="tsr-signup-field full-width">
            <label htmlFor="profilePicture">Profile Picture</label>
            <div className="tsr-input-wrapper">
              <input
                type="file"
                id="profilePicture"
                name="profilePicture"
                onChange={handleFileChange}
                accept="image/jpeg,image/jpg,image/png,image/gif"
                disabled={isLoading}
              />
              {profilePreview && (
                <div className="tsr-profile-preview">
                  <img src={profilePreview} alt="Profile Preview" />
                  <button 
                    type="button" 
                    onClick={() => {
                      setForm({ ...form, profilePicture: null });
                      setProfilePreview(null);
                      document.getElementById('profilePicture').value = '';
                    }}
                    className="tsr-remove-image"
                    disabled={isLoading}
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
            <div className="tsr-file-info">
              Supported formats: JPEG, PNG, GIF. Max size: 5MB
            </div>
          </div>
        </div>

        <div className="tsr-signup-field" style={{display: 'none'}}>
          <input
            type="text"
            id="role"
            name="role"
            value={form.role}
            readOnly
          />
        </div>

        <button 
          type="submit" 
          className="tsr-signup-btn"
          disabled={isLoading}
        >
          {isLoading ? "Creating Account..." : "Create Account"}
        </button>

        <div className="tsr-divider">
          <span>or</span>
        </div>

        <div className="tsr-login-section">
          <p className="tsr-login-text">Already have an account?</p>
          <Link to="/login" className="tsr-login-link">
            <button type="button" className="tsr-login-btn">
              Sign In
            </button>
          </Link>
        </div>
      </form>
    </div>
  );
}

export default Signup;